export default class SummaryHandler {
    constructor(options = {}) {
        this.ellipsis = options.ellipsis || '...';
        this.selectors = options.selectors || {
            containers: '.summary-container',
            summary: '.summary',
            moreLink: '.summary-more',
            lessLink: '.summary-less',
        };
        this.mobileLineLimit = options.mobileLineLimit || 3;
        this.cache = new Map(); // Cache for line height measurements
        this.init();
    }

    init() {
        this.processAllSummaries();
        this.attachEventListeners();
        this.handleResize();
    }

    processAllSummaries() {
        const summaries = document.querySelectorAll(this.selectors.containers);
        summaries.forEach(summary => this.processSummary(summary));
    }

    processSummary(summaryElement) {
        const summary = summaryElement.querySelector(this.selectors.summary);
        const moreLink = summaryElement.querySelector(this.selectors.moreLink);
        const lessLink = summaryElement.querySelector(this.selectors.lessLink);

        if (!summary) return;

        const fullText = summary.textContent.trim();
        const isMobile = window.innerWidth <= 768;

        if (!isMobile) {
            const storedFullText = summary.dataset.fullText;
            if (storedFullText && summary.textContent !== storedFullText) {
                summary.textContent = storedFullText;
            }
            
            if (moreLink) moreLink.style.display = 'none';
            if (lessLink) lessLink.style.display = 'none';
            summaryElement.classList.remove('is-truncated');
            summaryElement.classList.remove('is-expanded');
            return;
        }

        this.processMobileSummary(summaryElement, summary, moreLink, lessLink, fullText);
    }

    // Cache line height measurements to avoid repeated calculations
    getLineHeight(element) {
        const cacheKey = `${element.className}_${window.innerWidth}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }
        
        const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
        this.cache.set(cacheKey, lineHeight);
        return lineHeight;
    }

    processMobileSummary(summaryElement, summary, moreLink, lessLink, fullText) {
        summary.dataset.fullText = fullText;

        const lineHeight = this.getLineHeight(summary);
        const maxHeight = lineHeight * this.mobileLineLimit;

        if (fullText.length < 100) {
            summary.textContent = fullText;
            if (summary.scrollHeight <= maxHeight) {
                this.hideMoreLessLinks(moreLink, lessLink, summaryElement);
                return;
            }
        }

        const truncatedText = this.getTruncatedText(summary, fullText, maxHeight);
        
        if (truncatedText === fullText) {
            this.hideMoreLessLinks(moreLink, lessLink, summaryElement);
        } else {
            summary.textContent = truncatedText;
            this.showMoreLessLinks(moreLink, lessLink, summaryElement, true);
        }
    }

    getTruncatedText(summary, fullText, maxHeight) {
        const containerWidth = summary.offsetWidth || 300; // fallback width
        const avgCharWidth = 8; // approximate character width in pixels
        const charsPerLine = Math.floor(containerWidth / avgCharWidth);
        const estimatedLines = Math.ceil(fullText.length / charsPerLine);
        
        if (estimatedLines <= this.mobileLineLimit) {
            return fullText;
        }

        const charLimit = charsPerLine * this.mobileLineLimit;
        
        let truncatedText = fullText.substring(0, charLimit);
        const lastSpaceIndex = truncatedText.lastIndexOf(' ');
        
        if (lastSpaceIndex > charLimit * 0.8) {
            truncatedText = truncatedText.substring(0, lastSpaceIndex);
        }
        
        truncatedText += this.ellipsis;
        
        summary.textContent = truncatedText;
        if (summary.scrollHeight > maxHeight) {
            const newCharLimit = Math.floor(charLimit * 0.9);
            truncatedText = fullText.substring(0, newCharLimit);
            const lastSpaceIndex = truncatedText.lastIndexOf(' ');
            if (lastSpaceIndex > newCharLimit * 0.8) {
                truncatedText = truncatedText.substring(0, lastSpaceIndex);
            }
            truncatedText += this.ellipsis;
        }
        
        return truncatedText;
    }

    hideMoreLessLinks(moreLink, lessLink, summaryElement) {
        if (moreLink) moreLink.style.display = 'none';
        if (lessLink) lessLink.style.display = 'none';
        summaryElement.classList.remove('is-truncated');
        summaryElement.classList.remove('is-expanded');
    }

    showMoreLessLinks(moreLink, lessLink, summaryElement, showMore = true) {
        if (moreLink) moreLink.style.display = showMore ? 'inline' : 'none';
        if (lessLink) lessLink.style.display = showMore ? 'none' : 'inline';
        summaryElement.classList.toggle('is-truncated', showMore);
        summaryElement.classList.toggle('is-expanded', !showMore);
    }

    attachEventListeners() {
        document.addEventListener('touchstart', (e) => {
            const isMoreLink = e.target.matches(this.selectors.moreLink);
            const isLessLink = e.target.matches(this.selectors.lessLink);

            if (isMoreLink || isLessLink) {
                e.preventDefault();
                const container = e.target.closest(this.selectors.containers);
                if (container) {
                    this.toggleSummary(container, isMoreLink);
                    // Trigger GA4 analytics event
                    this.triggerGA4Event(e.target);
                }
            }
        }, { passive: false });
    }

    triggerGA4Event(elementTarget) {
        const defaultData = {
            'event': 'on_page_link_click',
            'elementPlacement': elementTarget.dataset.elementplacement || 'summary-expand',
            'elementClicked': elementTarget.dataset.elementclicked || '',
            'pageUrl': window.location.href,
            'position': elementTarget.dataset.order || 'single',
            'linkText': elementTarget.innerText || '',
            'direction': elementTarget.dataset.direction || '',
        };
        
        window.dataLayer.push(defaultData);
        console.log(defaultData);
    }

    toggleSummary(summaryElement, isExpanding) {
        const summary = summaryElement.querySelector(this.selectors.summary);
        const moreLink = summaryElement.querySelector(this.selectors.moreLink);
        const lessLink = summaryElement.querySelector(this.selectors.lessLink);

        if (isExpanding) {
            // Show full text
            const fullText = summary.dataset.fullText;
            if (fullText) {
                summary.textContent = fullText;
            }
            this.showMoreLessLinks(moreLink, lessLink, summaryElement, false);
        } else {
            // Show truncated text
            this.processSummary(summaryElement);
        }
    }

    handleResize() {
        let resizeTimeout;
        let lastWidth = window.innerWidth;
        
        window.addEventListener('resize', () => {
            const currentWidth = window.innerWidth;
            
            // Only reprocess if the width has actually changed significantly
            // This prevents reprocessing during mobile scroll events
            if (Math.abs(currentWidth - lastWidth) > 10) {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.cache.clear();
                    this.processAllSummaries();
                }, 250);
                lastWidth = currentWidth;
            }
        });
    }

    // Method to process new content after AJAX updates
    processNewContent(container) {
        const summaries = container.querySelectorAll(this.selectors.containers);
        summaries.forEach(summary => this.processSummary(summary));
    }
}
